// Supply shell sidebar + Home strings — EN + TR.
//
// Wave-1 Day 2 wiring. SO2 (Production Partner) + SO3 (Vendor) persona
// branches both wired. Persona description strings from `store/role.ts`
// SUPPLY_PERSONAS constant are out-of-scope V1 (Wave-5 re-audit migrates).

import type { LocalizedDict } from '../index'

type SupplyShellStrings = {
  titlePrefix: string
  personaLabel: string
  switchPersona: string
  fallbackName: string
  items: {
    overview: string
    workOrders: string
    poInbox: string
    submitNewUpdate: string
    mySubmittedUpdates: string
    activity: string
    settings: string
    resources: string
    myPermissions: string
    todaysPrintQueue: string
    preparePack: string
    samples: string
  }
  home: {
    pageTitle: string
    pageDescriptionPrefix: string
    pageDescriptionInfix: string
    pageDescriptionSuffix: string
    nextPickupLabel: string
    liveLabel: string
    metrics: {
      activeWos: string
      completedToday: string
      pendingQc: string
      onTimeRate: string
    }
    announcementEyebrow: string
    announcementTitle: string
    announcementBody: string
    announcementCta: string
    announcementToastDescription: string
    quickActionsHeader: string
    quickActionsHint: string
    quickActions: {
      workOrdersToStart: string
      qcPending: string
      qualityIssue: string
      shipmentReady: string
    }
    quickActionsToastTitle: string
    quickActionsToastDescriptions: {
      workOrderQueue: string
      qualityCheckQueue: string
      qualityIncident: string
      readyToShip: string
    }
    so3BodyCardTitle: string
    so3BodyCardBody: string
    so2BodyCardTitle: string
    so2BodyCardParagraph1: string
    so2BodyCardParagraph2Prefix: string
    so2BodyCardParagraph2Body: string
    m1ScopeStrong: string
    m1ScopeBody: string
  }
}

export const supplyShellI18n: LocalizedDict<SupplyShellStrings> = {
  en: {
    titlePrefix: 'Supply',
    personaLabel: 'Supply persona:',
    switchPersona: 'Switch supply persona',
    fallbackName: 'Supply (demo)',
    items: {
      overview: 'Overview',
      workOrders: 'My Work Orders',
      poInbox: 'PO Inbox',
      submitNewUpdate: 'Submit New Update',
      mySubmittedUpdates: 'My Submitted Updates',
      activity: 'Activity',
      settings: 'Settings',
      resources: 'Resources',
      myPermissions: 'My Permissions',
      todaysPrintQueue: "Today's Print Queue",
      preparePack: 'Prepare & Pack',
      samples: 'Samples',
    },
    home: {
      pageTitle: 'Supply Dashboard',
      pageDescriptionPrefix: 'D-7 supply shell — currently ',
      pageDescriptionInfix: ' perspective active — ',
      pageDescriptionSuffix: '.',
      nextPickupLabel: 'Next pickup',
      liveLabel: '6 active operators',
      metrics: {
        activeWos: 'Active WOs',
        completedToday: 'Completed today',
        pendingQc: 'Pending QC',
        onTimeRate: 'On-time rate',
      },
      announcementEyebrow: 'M1 Simulation',
      announcementTitle: 'M1 simulation Live',
      announcementBody:
        '5+ features added today — Order Management 10-panel, Settings overlay. Real BE switches in once M3 SPEC.md Faz 1 is ratified.',
      announcementCta: 'Learn more',
      announcementToastDescription:
        'Operational simulation active — backend persistence after M3 SPEC.md.',
      quickActionsHeader: 'Quick actions',
      quickActionsHint: 'production priorities',
      quickActions: {
        workOrdersToStart: '3 work orders to start',
        qcPending: '2 QC pending',
        qualityIssue: '1 quality issue',
        shipmentReady: 'Shipment ready',
      },
      quickActionsToastTitle: 'M1 mock action',
      quickActionsToastDescriptions: {
        workOrderQueue: 'Work order queue.',
        qualityCheckQueue: 'Quality check queue.',
        qualityIncident: 'Quality incident review.',
        readyToShip: 'Ready-to-ship queue.',
      },
      so3BodyCardTitle: 'IQ-1 cross-side loop',
      so3BodyCardBody:
        'As an SO3 vendor send master catalog update suggestions; admin approves or rejects. Status tracking is in sidebar My Submitted Updates.',
      so2BodyCardTitle: 'SO2 Production Partner',
      so2BodyCardParagraph1:
        'View production work orders assigned by admin. State lifecycle: pending → acknowledged → in_progress → quality_check → completed.',
      so2BodyCardParagraph2Prefix: 'B-6 identity-separation:',
      so2BodyCardParagraph2Body:
        ' SO2 partner account is separate from the SO3 vendor account — the same person may operate under two accounts in different operational scopes.',
      m1ScopeStrong: 'Current M1 supply scope:',
      m1ScopeBody:
        ' 3 personas (Aybars SO3 + Mehmet SO2 + Hakan SO2). M2+: SO1 own production (PEXIFY internal workshop) + workflow state transition actions (partner ack / mark in_progress / QC etc.).',
    },
  },
  tr: {
    titlePrefix: 'Üretim',
    personaLabel: 'Üretim persona:',
    switchPersona: 'Üretim personasını değiştir',
    fallbackName: 'Üretim (demo)',
    items: {
      overview: 'Genel Bakış',
      workOrders: 'İş Emirlerim',
      poInbox: 'PO Gelen Kutusu',
      submitNewUpdate: 'Yeni Güncelleme Gönder',
      mySubmittedUpdates: 'Gönderilen Güncellemelerim',
      activity: 'Aktivite',
      settings: 'Ayarlar',
      resources: 'Kaynaklar',
      myPermissions: 'Yetkilerim',
      todaysPrintQueue: 'Bugünün Yazdırma Kuyruğu',
      preparePack: 'Hazırla & Paketle',
      samples: 'Numuneler',
    },
    home: {
      pageTitle: 'Üretim Paneli',
      pageDescriptionPrefix: 'D-7 üretim paneli — şu an ',
      pageDescriptionInfix: ' perspektifi aktif — ',
      pageDescriptionSuffix: '.',
      nextPickupLabel: 'Sıradaki teslim alma',
      liveLabel: '6 aktif operatör',
      metrics: {
        activeWos: 'Aktif İş Emirleri',
        completedToday: 'Bugün tamamlanan',
        pendingQc: 'Bekleyen QC',
        onTimeRate: 'Zamanında oran',
      },
      announcementEyebrow: 'M1 Simülasyon',
      announcementTitle: 'M1 simülasyon canlı',
      announcementBody:
        'Bugün eklenen 5+ özellik — Sipariş Yönetimi 10-panel, Ayarlar katmanı. M3 SPEC.md Faz 1 ratify ile gerçek BE devreye girer.',
      announcementCta: 'Bilgi al',
      announcementToastDescription:
        'Operasyonel simülasyon aktif — backend kalıcılaşması M3 SPEC.md sonrası.',
      quickActionsHeader: 'Hızlı işlemler',
      quickActionsHint: 'üretim öncelikleri',
      quickActions: {
        workOrdersToStart: '3 iş emri başlatılacak',
        qcPending: '2 QC bekliyor',
        qualityIssue: '1 kalite sorunu',
        shipmentReady: 'Sevkiyat hazır',
      },
      quickActionsToastTitle: 'M1 mock işlem',
      quickActionsToastDescriptions: {
        workOrderQueue: 'İş emri kuyruğu.',
        qualityCheckQueue: 'Kalite kontrol kuyruğu.',
        qualityIncident: 'Kalite olayı incelemesi.',
        readyToShip: 'Sevke hazır kuyruğu.',
      },
      so3BodyCardTitle: 'IQ-1 cross-side döngü',
      so3BodyCardBody:
        'SO3 olarak master kataloğa güncelleme önerisi gönder; admin onaylar veya reddeder. Durum takibi sidebar\'daki Gönderilen Güncellemeler\'de.',
      so2BodyCardTitle: 'SO2 Üretim Ortağı',
      so2BodyCardParagraph1:
        'Admin tarafından atanan üretim iş emirlerini gör. Durum yaşam döngüsü: pending → acknowledged → in_progress → quality_check → completed.',
      so2BodyCardParagraph2Prefix: 'B-6 kimlik ayrımı:',
      so2BodyCardParagraph2Body:
        ' SO2 partner hesabın SO3 hesabından ayrıdır — aynı kişi iki hesap ile farklı operasyonel kapsamda çalışabilir.',
      m1ScopeStrong: 'Mevcut M1 tedarik kapsamı:',
      m1ScopeBody:
        ' 3 persona (Aybars SO3 + Mehmet SO2 + Hakan SO2). M2+: SO1 kendi üretimi (PEXIFY iç atölye) + iş akışı durum geçiş aksiyonları (partner kabul / in_progress işaretle / QC vb.).',
    },
  },
}
